import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RowModel, RowModelTableMetadata } from '@cancercentrum/rcc-react';
import { Explanation } from '@cancercentrum/rcc-react';
import type { Comparator } from 'immutable';
import { useModuleContext } from './ModuleContext.ts';
import IPOSubTableGrid from './components/IPOSubTableGrid.tsx';
import { isDirty } from './utils/rowmodel.ts';
import ReadOnlyIconButton from './modules/pdl/ReadOnlyIconButton.tsx';
import type { GridColumn, OverviewState } from './types.ts';
import { useIPO } from './hooks/useIPO.ts';
import type { ExternalStudiesResult } from './modules/studie/index.ts';

export interface ModuleGridProps<TRow extends RowModel> {
    columns: GridColumn<TRow, string>[];
    dateColumnIndex?: number;
    renderHeader?: (data: {
        row: TRow
    }) => string;
    allowEditingForAllPositions?: boolean;
    defaultSortFn?: Comparator<RowModel>;
    externalStudiesData?: ExternalStudiesResult;
}

function ModuleGrid<T extends RowModelTableMetadata>(props: ModuleGridProps<RowModel<T>>) {
    const {
        columns,
        dateColumnIndex = 0,
        renderHeader,
        allowEditingForAllPositions,
        defaultSortFn,
        externalStudiesData,
    } = props;
    const ctx = useModuleContext();

    if (!ctx.tableName || !ctx.gridId) {
        throw new Error('The module context is missing tableName and gridId.');
    }

    const selector = useCallback((state: OverviewState) => state.data.present.getSubTable(ctx.tableName as any), [ctx.tableName]);
    const subTable = useSelector(selector);
    const ipo = useIPO();
    const allColumns: GridColumn<RowModel<T>, string>[] = useMemo(() => {
        return [
            {
                id: 'row_status',
                searchable: false,
                sortable: false,
                tdProps: {
                    className: 'nostretch',
                },
                renderCell(data) {
                    const { row } = data;
                    if (isDirty(row)) {
                        return <Explanation>Raden har osparade ändringar.</Explanation>;
                    }

                    if (!allowEditingForAllPositions && row.$extra.dataRowOwnerPositionId !== ipo.currentUser.position.id) {
                        return <ReadOnlyIconButton dataRowOwnerId={row.$extra.dataRowOwnerPositionId} />;
                    }

                    return null;
                },
            },
            ...columns,
        ];
    }, [columns, allowEditingForAllPositions, ipo.currentUser.position.id]);

    return (
        <IPOSubTableGrid
            allowEditingForAllPositions={allowEditingForAllPositions}
            gridId={ctx.gridId}
            subTable={subTable}
            columns={allColumns as any}
            dateColumn={columns[dateColumnIndex] as any}
            renderHeader={renderHeader as any}
            defaultSortFn={defaultSortFn}
            externalStudiesData={externalStudiesData}
        />
    );
}

export default ModuleGrid;
